/**
 * Карточка управления дочерним ботом (owner).
 * Статус + действия (стоп/старт/бан/разбан) + клиенты бота.
 * Статусы: ACTION (работает) / STOP (пауза) / BANNED (бан) / DELETE_USER|DELETE_ADMIN (удалён).
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bot as BotIcon,
  Users,
  Loader2,
  Play,
  Pause,
  Ban,
  ShieldCheck,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { getWebApp, haptic } from '@/lib/telegram';
import { toast } from 'sonner';

export const Route = createFileRoute('/bots/$id')({ component: BotDetail });

function BotDetail() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const { data: b, isPending } = useQuery({
    queryKey: ['bot', id],
    queryFn: () => api.botDetail(id),
  });

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.show();
    const onBack = () => navigate({ to: '/partners' });
    tg?.BackButton.onClick(onBack);
    return () => {
      tg?.BackButton.offClick(onBack);
      tg?.BackButton.hide();
    };
  }, [navigate]);

  const { mutate: act, isPending: acting } = useMutation({
    mutationFn: (action: 'stop' | 'start' | 'ban' | 'unban') => api.botAction(id, action),
    onSuccess: (_res, action) => {
      qc.invalidateQueries({ queryKey: ['bot', id] });
      haptic(action === 'ban' || action === 'stop' ? 'warning' : 'success');
      const labels: Record<string, string> = {
        stop: 'Бот остановлен',
        start: 'Бот запущен',
        ban: 'Бот забанен',
        unban: 'Бот разбанен',
      };
      toast.success(labels[action]);
    },
    onError: (e) => {
      haptic('error');
      toast.error(apiErrorMessage(e) ?? 'Не удалось');
    },
  });

  const { data: clients } = useQuery({
    queryKey: ['bot-clients', b?.partner_id, id],
    queryFn: () => api.partnerClients(b!.partner_id, id),
    enabled: !!b?.partner_id,
  });

  if (isPending || !b) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const isDeleted = b.status === 'DELETE_USER' || b.status === 'DELETE_ADMIN';
  const isBanned = b.status === 'BANNED';
  const isStopped = b.status === 'STOP';
  const isActive = b.status === 'ACTION';

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <BotIcon className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold tracking-tight truncate">@{b.username}</h2>
          <p className="text-xs text-muted-foreground truncate">{b.title}</p>
        </div>
        <BotStatusBadge status={b.status} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat icon={<Users className="w-4 h-4" />} label="Клиентов" value={String(b.clients_count)} />
        <Stat icon={<Users className="w-4 h-4" />} label="Active subs" value={String(b.active_subscriptions)} />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <Link
            to="/partners/$id"
            params={{ id: b.partner_id }}
            preload="intent"
            className="flex items-center justify-between active:opacity-70"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Партнёр
            </span>
            <span className="text-sm flex items-center gap-1">
              {b.partner_name || b.partner_id.slice(0, 8)}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </span>
          </Link>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Открыть в Telegram
            </span>
            <a
              href={`https://t.me/${b.username}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary flex items-center gap-1"
            >
              @{b.username}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Создан
            </span>
            <span className="text-sm">{new Date(b.created_at).toLocaleDateString('ru')}</span>
          </div>
        </CardContent>
      </Card>

      {/* Действия зависят от статуса */}
      <div className="space-y-2">
        {isActive && (
          <div className="grid grid-cols-2 gap-2">
            <ActionBtn
              onClick={() => act('stop')}
              disabled={acting}
              cls="bg-yellow-500/15 text-yellow-500"
              icon={<Pause className="w-4 h-4" />}
              label="Остановить"
            />
            <ActionBtn
              onClick={() => act('ban')}
              disabled={acting}
              cls="bg-red-500/15 text-red-500"
              icon={<Ban className="w-4 h-4" />}
              label="Забанить"
            />
          </div>
        )}
        {isStopped && (
          <div className="grid grid-cols-2 gap-2">
            <ActionBtn
              onClick={() => act('start')}
              disabled={acting}
              cls="bg-green-500/15 text-green-500"
              icon={<Play className="w-4 h-4" />}
              label="Запустить"
            />
            <ActionBtn
              onClick={() => act('ban')}
              disabled={acting}
              cls="bg-red-500/15 text-red-500"
              icon={<Ban className="w-4 h-4" />}
              label="Забанить"
            />
          </div>
        )}
        {isBanned && (
          <ActionBtn
            onClick={() => act('unban')}
            disabled={acting}
            cls="bg-green-500 text-white w-full"
            icon={<ShieldCheck className="w-4 h-4" />}
            label="Разбанить"
          />
        )}
        {isDeleted && (
          <ActionBtn
            onClick={() => act('start')}
            disabled={acting}
            cls="bg-green-500 text-white w-full"
            icon={<ShieldCheck className="w-4 h-4" />}
            label="Восстановить (запустить)"
          />
        )}
        {acting && (
          <div className="flex justify-center py-1">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-5 space-y-1">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Клиенты бота {clients ? `(${clients.length})` : ''}
          </h3>
          {!clients ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          ) : clients.length === 0 ? (
            <p className="text-sm text-muted-foreground py-1">Нет клиентов</p>
          ) : (
            clients.map((c) => (
              <Link
                key={c.id}
                to="/clients/$id"
                params={{ id: c.id }}
                preload="intent"
                className="flex items-center gap-2 py-2 border-b border-white/5 last:border-0 active:opacity-70"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {c.tg_username ? `@${c.tg_username}` : `tg_id ${c.tg_user_id}`}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {c.active_subscriptions} актив. подписок
                    {c.is_blocked ? ' · заблокировал бота' : !c.is_active ? ' · бан' : ''}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </Link>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActionBtn({
  onClick,
  disabled,
  cls,
  icon,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  cls: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${cls}`}
    >
      {icon}
      {label}
    </button>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="bg-card border-white/8">
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="text-primary">{icon}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-lg font-bold truncate">{value}</div>
      </CardContent>
    </Card>
  );
}

function BotStatusBadge({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    ACTION: 'bg-green-500/15 text-green-500',
    STOP: 'bg-yellow-500/15 text-yellow-500',
    BANNED: 'bg-red-500/15 text-red-500',
    DELETE_USER: 'bg-white/10 text-muted-foreground',
    DELETE_ADMIN: 'bg-white/10 text-muted-foreground',
  };
  const label: Record<string, string> = {
    ACTION: 'работает',
    STOP: 'пауза',
    BANNED: 'бан',
    DELETE_USER: 'удалён',
    DELETE_ADMIN: 'удалён',
  };
  const cls = cfg[status] ?? 'bg-white/10 text-muted-foreground';
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${cls} shrink-0`}>
      {label[status] ?? status}
    </span>
  );
}
